import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { 
  getFollowers, 
  getFollowingList, 
  followUser as followUserService, 
  unfollowUser as unfollowUserService,
  getConnectionCounts
} from '../services/connections';

// Cache entry interfaces
interface CachedConnectionData {
  followers: string[];
  following: string[];
  followerCount: number;
  followingCount: number;
  cachedAt: number;
  userId: string;
}

// Store interface
export interface ConnectionStore {
  // State
  connections: Record<string, CachedConnectionData>;
  isLoading: boolean;
  loadingOperations: string[]; // Track specific operations like 'follow-userId', 'unfollow-userId'
  lastCleanup: number;
  
  // Actions
  getConnections: (userId: string) => CachedConnectionData | null;
  loadConnections: (userId: string) => Promise<void>;
  followUser: (currentUserId: string, targetUserId: string) => Promise<void>;
  unfollowUser: (currentUserId: string, targetUserId: string) => Promise<void>;
  refreshConnections: (userId: string) => Promise<void>;
  invalidateConnections: (userId: string) => void;
  clearExpiredConnections: () => void;
  isConnectionsExpired: (userId: string) => boolean;
  
  // Helper getters
  getFollowerCount: (userId: string) => number;
  getFollowingCount: (userId: string) => number;
  getFollowers: (userId: string) => string[];
  getFollowing: (userId: string) => string[];
  isFollowing: (currentUserId: string, targetUserId: string) => boolean;
  
  // Internal actions
  setConnections: (userId: string, data: Omit<CachedConnectionData, 'cachedAt' | 'userId'>) => void;
  addLoadingOperation: (operation: string) => void;
  removeLoadingOperation: (operation: string) => void;
  setLoading: (loading: boolean) => void;
}

// Constants
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes in milliseconds
const CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds

export const useConnectionStore = create<ConnectionStore>()(
  persist(
    (set, get) => ({
      // Initial state
      connections: {},
      isLoading: false,
      loadingOperations: [],
      lastCleanup: Date.now(),

      // Get cached connections or null if expired/missing
      getConnections: (userId: string) => {
        const state = get();
        const connections = state.connections[userId];
        
        if (!connections) return null;
        
        // Check if expired
        const isExpired = Date.now() - connections.cachedAt > CACHE_TTL;
        if (isExpired) {
          // Remove expired connections
          const newConnections = { ...state.connections };
          delete newConnections[userId];
          set({ connections: newConnections });
          return null;
        }
        
        return connections;
      },

      // Check if connections are expired without removing them
      isConnectionsExpired: (userId: string) => {
        const connections = get().connections[userId];
        if (!connections) return true;
        return Date.now() - connections.cachedAt > CACHE_TTL;
      },

      // Load connections from Firebase
      loadConnections: async (userId: string) => {
        const state = get();
        
        // Check if already cached and not expired
        const cached = state.getConnections(userId);
        if (cached) return;
        
        // Check if already loading
        if (state.loadingOperations.includes(`load-${userId}`)) return;
        
        const operationId = `load-${userId}`;
        state.addLoadingOperation(operationId);
        
        try {
          // console.log(`🔄 Loading connections for user: ${userId}`);
          
          const [followers, following] = await Promise.all([
            getFollowers(userId),
            getFollowingList(userId)
          ]);
          
          const connectionData = {
            followers,
            following,
            followerCount: followers.length,
            followingCount: following.length
          };
          
          state.setConnections(userId, connectionData);
          // console.log(`✅ Loaded connections for user: ${userId}`, connectionData);
          
        } catch (error) {
          console.error(`❌ Error loading connections for user: ${userId}`, error);
          throw error;
        } finally {
          state.removeLoadingOperation(operationId);
        }
      },

      // Follow a user with optimistic updates
      followUser: async (currentUserId: string, targetUserId: string) => {
        const state = get();
        const operationId = `follow-${targetUserId}`;
        
        // Check if already processing this operation
        if (state.loadingOperations.includes(operationId)) return;
        
        state.addLoadingOperation(operationId);
        
        try {
          // Optimistic update - update UI immediately
          const currentConnections = state.connections[currentUserId];
          if (currentConnections) {
            const updatedFollowing = [...currentConnections.following];
            if (!updatedFollowing.includes(targetUserId)) {
              updatedFollowing.push(targetUserId);
              
              state.setConnections(currentUserId, {
                ...currentConnections,
                following: updatedFollowing,
                followingCount: updatedFollowing.length
              });
            }
          }
          
          // Update target user's follower count optimistically if cached
          const targetConnections = state.connections[targetUserId];
          if (targetConnections) {
            const updatedFollowers = [...targetConnections.followers];
            if (!updatedFollowers.includes(currentUserId)) {
              updatedFollowers.push(currentUserId);
              
              state.setConnections(targetUserId, {
                ...targetConnections,
                followers: updatedFollowers,
                followerCount: updatedFollowers.length
              });
            }
          }
          
          console.log(`🔄 Following user: ${targetUserId}`);
          
          // Perform actual Firebase operation
          await followUserService(currentUserId, targetUserId);
          
          console.log(`✅ Successfully followed user: ${targetUserId}`);
          
        } catch (error) {
          console.error(`❌ Error following user: ${targetUserId}`, error);
          
          // Revert optimistic updates on error
          await state.refreshConnections(currentUserId);
          if (state.connections[targetUserId]) {
            await state.refreshConnections(targetUserId);
          }
          
          throw error;
        } finally {
          state.removeLoadingOperation(operationId);
        }
      },

      // Unfollow a user with optimistic updates
      unfollowUser: async (currentUserId: string, targetUserId: string) => {
        const state = get();
        const operationId = `unfollow-${targetUserId}`;
        
        // Check if already processing this operation
        if (state.loadingOperations.includes(operationId)) return;
        
        state.addLoadingOperation(operationId);
        
        try {
          // Optimistic update - update UI immediately
          const currentConnections = state.connections[currentUserId];
          if (currentConnections) {
            const updatedFollowing = currentConnections.following.filter(id => id !== targetUserId);
            
            state.setConnections(currentUserId, {
              ...currentConnections,
              following: updatedFollowing,
              followingCount: updatedFollowing.length
            });
          }
          
          // Update target user's follower count optimistically if cached
          const targetConnections = state.connections[targetUserId];
          if (targetConnections) {
            const updatedFollowers = targetConnections.followers.filter(id => id !== currentUserId);
            
            state.setConnections(targetUserId, {
              ...targetConnections,
              followers: updatedFollowers,
              followerCount: updatedFollowers.length
            });
          }
          
          console.log(`🔄 Unfollowing user: ${targetUserId}`);
          
          // Perform actual Firebase operation
          await unfollowUserService(currentUserId, targetUserId);
          
          console.log(`✅ Successfully unfollowed user: ${targetUserId}`);
          
        } catch (error) {
          console.error(`❌ Error unfollowing user: ${targetUserId}`, error);
          
          // Revert optimistic updates on error
          await state.refreshConnections(currentUserId);
          if (state.connections[targetUserId]) {
            await state.refreshConnections(targetUserId);
          }
          
          throw error;
        } finally {
          state.removeLoadingOperation(operationId);
        }
      },

      // Refresh connections from Firebase (force reload)
      refreshConnections: async (userId: string) => {
        const state = get();
        
        // Remove from cache to force fresh load
        const newConnections = { ...state.connections };
        delete newConnections[userId];
        set({ connections: newConnections });
        
        // Load fresh data
        await state.loadConnections(userId);
      },

      // Invalidate connections (remove from cache)
      invalidateConnections: (userId: string) => {
        const newConnections = { ...get().connections };
        delete newConnections[userId];
        set({ connections: newConnections });
      },

      // Clean up expired connections
      clearExpiredConnections: () => {
        const state = get();
        const now = Date.now();
        
        // Only run cleanup if enough time has passed
        if (now - state.lastCleanup < CLEANUP_INTERVAL) return;
        
        const newConnections: Record<string, CachedConnectionData> = {};
        let removedCount = 0;
        
        // Keep only non-expired connections
        Object.entries(state.connections).forEach(([userId, connections]) => {
          if (now - connections.cachedAt <= CACHE_TTL) {
            newConnections[userId] = connections;
          } else {
            removedCount++;
          }
        });
        
        if (removedCount > 0) {
          console.log(`🧹 Cleaned up ${removedCount} expired connection records`);
          set({ 
            connections: newConnections,
            lastCleanup: now
          });
        } else {
          set({ lastCleanup: now });
        }
      },

      // Helper getters
      getFollowerCount: (userId: string) => {
        const connections = get().getConnections(userId);
        return connections?.followerCount || 0;
      },

      getFollowingCount: (userId: string) => {
        const connections = get().getConnections(userId);
        return connections?.followingCount || 0;
      },

      getFollowers: (userId: string) => {
        const connections = get().getConnections(userId);
        return connections?.followers || [];
      },

      getFollowing: (userId: string) => {
        const connections = get().getConnections(userId);
        return connections?.following || [];
      },

      isFollowing: (currentUserId: string, targetUserId: string) => {
        const connections = get().getConnections(currentUserId);
        return connections?.following.includes(targetUserId) || false;
      },

      // Internal actions
      setConnections: (userId: string, data: Omit<CachedConnectionData, 'cachedAt' | 'userId'>) => {
        const newConnections = { ...get().connections };
        newConnections[userId] = {
          ...data,
          userId,
          cachedAt: Date.now()
        };
        set({ connections: newConnections });
      },

      addLoadingOperation: (operation: string) => {
        const currentOps = get().loadingOperations;
        if (!currentOps.includes(operation)) {
          set({ 
            loadingOperations: [...currentOps, operation],
            isLoading: true
          });
        }
      },

      removeLoadingOperation: (operation: string) => {
        const newOps = get().loadingOperations.filter(op => op !== operation);
        set({ 
          loadingOperations: newOps,
          isLoading: newOps.length > 0
        });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      }
    }),
    {
      name: 'connections-storage',
      storage: createJSONStorage(() => localStorage),

      // Partial persist - only save connections and lastCleanup to localStorage
      partialize: (state) => ({
        connections: state.connections,
        lastCleanup: state.lastCleanup
      }),

      // Version for future migrations
      version: 1
    }
  )
);

// Hook for connection management
export const useConnections = (userId: string | null) => {
  const store = useConnectionStore();
  
  // Get cached connections
  const connections = userId ? store.getConnections(userId) : null;
  
  // Auto-load if not cached and userId provided
  if (userId && !connections && !store.loadingOperations.includes(`load-${userId}`)) {
    store.loadConnections(userId);
  }
  
  return {
    // Data
    connections,
    followers: connections?.followers || [],
    following: connections?.following || [],
    followerCount: connections?.followerCount || 0,
    followingCount: connections?.followingCount || 0,
    
    // Loading states
    isLoading: userId ? store.loadingOperations.some(op => op.startsWith(`load-${userId}`)) : false,
    isFollowLoading: (targetUserId: string) => store.loadingOperations.includes(`follow-${targetUserId}`),
    isUnfollowLoading: (targetUserId: string) => store.loadingOperations.includes(`unfollow-${targetUserId}`),
    
    // Actions
    followUser: (targetUserId: string) => userId ? store.followUser(userId, targetUserId) : Promise.resolve(),
    unfollowUser: (targetUserId: string) => userId ? store.unfollowUser(userId, targetUserId) : Promise.resolve(),
    refresh: () => userId ? store.refreshConnections(userId) : Promise.resolve(),
    invalidate: () => userId ? store.invalidateConnections(userId) : undefined,
    
    // Helpers
    isFollowing: (targetUserId: string) => userId ? store.isFollowing(userId, targetUserId) : false
  };
};

// Hook for connection utilities
export const useConnectionUtils = () => {
  const store = useConnectionStore();
  
  return {
    getConnections: store.getConnections,
    loadConnections: store.loadConnections,
    clearExpired: store.clearExpiredConnections,
    isLoading: store.isLoading,
    loadingOperations: store.loadingOperations,
    connectionCount: Object.keys(store.connections).length
  };
};